/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Search, ShieldAlert, Database, Radar } from "lucide-react";

/**
 * Full-screen overlay shown while POST /api/scan/free is in flight.
 *
 * The backend can take ~15-20s (16 real HTTP probes to Chilean rutificadores
 * running in parallel) so we surface progress with a rotating status line and
 * a checklist of stages. The overlay blocks interaction so the visitor
 * doesn't refresh mid-scan.
 */
export default function ScanLoading({ open }) {
  const stages = [
    { icon: Radar,        label: "Analizando y buscando tus datos públicos…" },
    { icon: Search,       label: "Consultando 16 rutificadores chilenos en paralelo…" },
    { icon: Database,     label: "Cotejando findatos.com, nombrerutyfirma, rutificador.cl…" },
    { icon: ShieldAlert,  label: "Verificando exposición en bases de datos masivas…" },
    { icon: Loader2,      label: "Preparando tu reporte con evidencia…" },
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!open) { setIdx(0); return; }
    const id = setInterval(() => setIdx((i) => (i + 1) % stages.length), 3200);
    return () => clearInterval(id);
  }, [open, stages.length]);

  const Stage = stages[idx];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[60] bg-[#0B2447]/95 backdrop-blur-sm flex items-center justify-center px-6"
          data-testid="scan-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-lg w-full text-center">
            {/* Radar animation */}
            <div className="relative mx-auto w-24 h-24 mb-8">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 2.4, ease: "linear", repeat: Infinity }}
                className="absolute inset-0 border-2 border-amber-300/30 rounded-full"
              />
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1.6, ease: "linear", repeat: Infinity }}
                className="absolute inset-3 border-2 border-t-amber-300 border-transparent rounded-full"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <Stage.icon className={`w-8 h-8 text-amber-300 ${Stage.icon === Loader2 ? "animate-spin" : ""}`} />
              </div>
            </div>

            <div className="eyebrow text-amber-300 mb-3">Escaneo en curso</div>

            <AnimatePresence mode="wait">
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35 }}
                className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight min-h-[3.5rem]"
                data-testid="scan-loading-message"
              >
                {Stage.label}
              </motion.div>
            </AnimatePresence>

            <p className="mt-5 text-sm text-white/70 leading-relaxed">
              Consultamos cada rutificador chileno en tiempo real. Esto toma
              entre <b>10 y 20 segundos</b>. No cierres esta pestaña.
            </p>

            {/* Checklist */}
            <div className="mt-8 flex flex-wrap gap-1.5 justify-center">
              {stages.map((s, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i <= idx ? "w-10 bg-amber-300" : "w-6 bg-white/20"
                  }`}
                />
              ))}
            </div>

            <div className="mt-8 inline-flex items-center gap-2 text-xs font-mono text-white/50 uppercase tracking-[0.15em]">
              <div className="w-1.5 h-1.5 bg-amber-300 rounded-full animate-pulse" />
              Conexiones cifradas · Ley 21.719
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
